import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

/** Set to true to show the theme toggle; when false, theme follows the device setting */
export const THEME_TOGGLE_ENABLED = false;

const ThemeContext = createContext({
  theme: 'dark',
  toggleTheme: () => {},
  setTheme: () => {},
});

function getSystemTheme() {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function getInitialTheme() {
  if (!THEME_TOGGLE_ENABLED) return getSystemTheme();
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem('timeline-theme');
  if (stored === 'light' || stored === 'dark') return stored;
  return getSystemTheme();
}

function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'light' ? '#f4f6f5' : '#0a0d0c');
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    if (!THEME_TOGGLE_ENABLED) {
      const media = window.matchMedia('(prefers-color-scheme: light)');
      const syncFromSystem = () => {
        const next = media.matches ? 'light' : 'dark';
        setThemeState(next);
        applyTheme(next);
      };

      syncFromSystem();
      media.addEventListener('change', syncFromSystem);
      return () => media.removeEventListener('change', syncFromSystem);
    }

    applyTheme(theme);
    window.localStorage.setItem('timeline-theme', theme);
    return undefined;
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      setTheme: (next) => {
        if (!THEME_TOGGLE_ENABLED) return;
        setThemeState(next);
      },
      toggleTheme: () => {
        if (!THEME_TOGGLE_ENABLED) return;
        setThemeState((t) => (t === 'dark' ? 'light' : 'dark'));
      },
    }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
